import { db, type Mandant } from '../db/schema';
import { toMandantID, type MandantID } from '../types/branded';
import { DEFAULT_MANDANTEN } from '../store/mandantStore';

export type { Mandant };

export async function loadMandanten(): Promise<Mandant[]> {
  return db.mandanten.orderBy('kurz_code').toArray();
}

export async function saveMandant(m: Mandant): Promise<void> {
  await db.mandanten.put(m);
}

export async function deleteMandant(id: MandantID): Promise<void> {
  await db.mandanten.delete(id);
}

export function buildMandant(input: {
  kurz_code:         string;
  name:              string;
  farbe:             string;
  email_domains:     string[];
  location_keywords: string[];
  title_aliases:     string[];
  strasse?:          string;
  plz?:              string;
  ort?:              string;
  land?:             string;
  telefon?:          string;
  email?:            string;
  hauptkontakt?:     { name: string; titel: string; email: string; telefon: string };
  branche?:          string;
  mitarbeiterzahl?:  number;
  bemerkungen?:      string;
}): Mandant {
  const id = toMandantID(`mandant-${input.kurz_code.toLowerCase()}`);
  return {
    id,
    schema_version: 1,
    kurz_code:         input.kurz_code.toUpperCase(),
    name:              input.name,
    farbe:             input.farbe,
    email_domains:     input.email_domains,
    location_keywords: input.location_keywords,
    title_aliases:     input.title_aliases,
    aktiv:             true,
    erstellt_am:       new Date().toISOString(),
    ...(input.strasse !== undefined && { strasse: input.strasse }),
    ...(input.plz !== undefined && { plz: input.plz }),
    ...(input.ort !== undefined && { ort: input.ort }),
    ...(input.land !== undefined && { land: input.land }),
    ...(input.telefon !== undefined && { telefon: input.telefon }),
    ...(input.email !== undefined && { email: input.email }),
    ...(input.hauptkontakt !== undefined && { hauptkontakt: input.hauptkontakt }),
    ...(input.branche !== undefined && { branche: input.branche }),
    ...(input.mitarbeiterzahl !== undefined && { mitarbeiterzahl: input.mitarbeiterzahl }),
    ...(input.bemerkungen !== undefined && { bemerkungen: input.bemerkungen }),
  };
}

// Seed-Mandanten: aus DEFAULT_MANDANTEN abgeleitet, damit Store und DB
// dieselben Stammdaten haben (vorher zwei divergierende Listen)
export const SEED_MANDANTEN: Mandant[] = DEFAULT_MANDANTEN.map(m => ({
  ...m,
  schema_version: 1,
  erstellt_am: '2026-01-01T00:00:00.000Z',
}));

const SEED_REPAIR_FLAG = 'amed-seed-repair-v1';

export async function seedMandantenIfEmpty(): Promise<void> {
  const count = await db.mandanten.count();
  if (count === 0) {
    await db.mandanten.bulkAdd(SEED_MANDANTEN);
    localStorage.setItem(SEED_REPAIR_FLAG, new Date().toISOString());
    return;
  }
  // Einmalige Reparatur: durch den früheren Store/DB-Sync-Bug konnten
  // Seed-Mandanten aus der DB verschwinden, während die Sidebar sie noch
  // zeigte. Fehlende Seeds einmalig nachsäen, danach nie wieder (Flag),
  // damit bewusst gelöschte Mandanten nicht wieder auftauchen.
  if (localStorage.getItem(SEED_REPAIR_FLAG) === null) {
    for (const m of SEED_MANDANTEN) {
      const existing = await db.mandanten.get(m.id);
      if (existing === undefined) {
        await db.mandanten.add(m);
      }
    }
    localStorage.setItem(SEED_REPAIR_FLAG, new Date().toISOString());
  }
}
